<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="sunfish-attendance-card">
    <!-- Header -->
    <div class="sf-header">
      <div class="sf-date-row">
        <span class="sf-date">Hari Ini ({{ formattedToday }})</span>
        <i class="oxd-icon bi-calendar-text sf-header-icon"></i>
      </div>
      <div class="sf-shift">
        Shift: Change Work Time 09:00-16:00 [09:00 - 16:00]
      </div>
    </div>

    <hr class="sf-divider" />

    <!-- Punch columns -->
    <div class="sf-columns">
      <!-- Jam Masuk -->
      <div class="sf-col">
        <div class="sf-avatar-wrapper">
          <div class="sf-avatar">BA</div>
          <div class="sf-label">Jam Masuk</div>
        </div>
        <div class="sf-time-row">
          <span class="sf-time">{{ punchInTimeDisplay }}</span>
          <span class="sf-icons">
            <i class="oxd-icon bi-geo-alt-fill sf-icon-red"></i>
            <i class="oxd-icon bi-person-bounding-box sf-icon-red"></i>
          </span>
        </div>
      </div>

      <!-- Jam Keluar -->
      <div class="sf-col sf-col-border">
        <div class="sf-avatar-wrapper">
          <div class="sf-avatar">BA</div>
          <div class="sf-label">Jam Keluar</div>
        </div>
        <div class="sf-time-row">
          <span class="sf-time">{{ punchOutTimeDisplay }}</span>
          <span class="sf-icons">
            <i class="oxd-icon bi-geo-alt-fill sf-icon-red"></i>
            <i class="oxd-icon bi-person-bounding-box sf-icon-red"></i>
          </span>
        </div>
      </div>
    </div>

    <!-- Geofence & Location status -->
    <div v-if="geofenceEnabled || locationError" class="sf-status-text">
      <span v-if="locationError" class="sf-text-red">{{ locationError }}</span>
      <span v-else-if="geofenceStatus === 'checking'"
        >Mencari lokasi GPS...</span
      >
      <span v-else-if="geofenceStatus === 'outside'" class="sf-text-red"
        >Di luar zona absen</span
      >
      <span v-else-if="geofenceStatus === 'inside'" class="sf-text-green"
        >Dalam zona absen ({{ nearestZone?.name }})</span
      >
    </div>

    <!-- Face Recognition Camera Section -->
    <div v-if="faceRecognitionEnabled" class="sf-camera-section">
      <div
        class="sf-face-status"
        :class="faceVerified ? 'sf-text-green' : 'sf-text-orange'"
      >
        {{
          faceVerified
            ? '✓ Wajah Terverifikasi'
            : 'Pindai Wajah Untuk Melanjutkan'
        }}
      </div>
      <face-recognition
        ref="faceRecognitionRef"
        :emp-number="currentEmpNumber"
        mode="verify"
        @verified="onFaceVerified"
        @failed="onFaceFailed"
        @no-descriptor="onNoDescriptor"
        @camera-error="onCameraError"
      />
    </div>

    <!-- Main Button -->
    <div class="sf-btn-container">
      <button
        class="sf-btn-rekam"
        :disabled="isSubmitDisabled || isLoading"
        @click="onSave"
      >
        <span v-if="isLoading">Menyimpan...</span>
        <span v-else>Rekam Waktu</span>
      </button>
    </div>

    <!-- Break Time -->
    <div class="sf-footer">
      <div class="sf-break-title">Waktu Istirahat 1:</div>
      <div class="sf-break-time">--:-- - --:--</div>

      <div class="sf-lainnya" @click="showDetails = !showDetails">Lainnya</div>
    </div>

    <hr class="sf-divider" />

    <div class="sf-sembunyikan" @click="showDetails = !showDetails">
      {{ showDetails ? 'Sembunyikan Detail ^' : 'Tampilkan Detail v' }}
    </div>

    <!-- Hidden standard form -->
    <div v-show="showDetails" class="sf-details-section">
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item class="--span-column-2">
              <date-input
                :key="attendanceRecord.time"
                v-model="attendanceRecord.date"
                :label="$t('general.date')"
                :rules="rules.date"
                :disabled="!isEditable"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item class="--span-column-2">
              <oxd-input-field
                v-model="attendanceRecord.time"
                :label="$t('general.time')"
                :disabled="!isEditable"
                :rules="rules.time"
                type="time"
                :placeholder="$t('attendance.hh_mm')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-grid v-if="isTimezoneEditable" :cols="2">
          <oxd-grid-item>
            <timezone-dropdown v-model="attendanceRecord.timezone" required />
          </oxd-grid-item>
        </oxd-grid>

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item class="--span-column-4">
              <oxd-input-field
                v-model="attendanceRecord.note"
                :rules="rules.note"
                :label="$t('general.note')"
                :placeholder="$t('general.type_here')"
                type="textarea"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {
  required,
  validDateFormat,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';
import {
  parseTime,
  parseDate,
  formatTime,
  formatDate,
  guessTimezone,
  setClockInterval,
  getStandardTimezone,
} from '@/core/util/helper/datefns';
import {promiseDebounce} from '@ohrm/oxd';
import useLocale from '@/core/util/composable/useLocale';
import {APIService} from '@ohrm/core/util/services/api.service';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {reloadPage, navigate} from '@/core/util/helper/navigation';
import TimezoneDropdown from '@/orangehrmAttendancePlugin/components/TimezoneDropdown.vue';
import FaceRecognition from '@/orangehrmAttendancePlugin/components/FaceRecognition.vue';

const attendanceRecordModal = {
  date: null,
  time: null,
  note: null,
  timezone: null,
  previousRecord: null,
  latitude: null,
  longitude: null,
  photo: null,
};

export default {
  name: 'RecordAttendance',
  components: {
    'timezone-dropdown': TimezoneDropdown,
    'face-recognition': FaceRecognition,
  },
  props: {
    isEditable: {
      type: Boolean,
      default: false,
    },
    isTimezoneEditable: {
      type: Boolean,
      default: false,
    },
    attendanceRecordId: {
      type: Number,
      default: null,
    },
    employeeId: {
      type: Number,
      default: null,
    },
    date: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const apiPath = props.employeeId
      ? `/api/v2/attendance/employees/${props.employeeId}/records`
      : '/api/v2/attendance/records';
    const http = new APIService(window.appGlobal.baseUrl, apiPath);
    const configHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/configs',
    );
    const geofenceHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/geofence',
    );
    const {jsDateFormat, userDateFormat, timeFormat, jsTimeFormat} =
      useDateFormat();
    const {locale} = useLocale();
    return {
      http,
      configHttp,
      geofenceHttp,
      locale,
      timeFormat,
      jsTimeFormat,
      jsDateFormat,
      userDateFormat,
    };
  },
  data() {
    return {
      isLoading: false,
      attendanceRecord: {...attendanceRecordModal},
      rules: {
        date: [
          required,
          validDateFormat(this.userDateFormat),
          promiseDebounce(this.validateDate, 500),
        ],
        time: [required, promiseDebounce(this.validateDate, 500)],
        note: [shouldNotExceedCharLength(250)],
      },
      previousRecordTimezone: null,
      locationError: null,
      stream: null,
      // ─── Geofence ───────────────────────────────────
      geofenceEnabled: false,
      geofenceStatus: 'checking', // 'checking' | 'inside' | 'outside' | 'disabled'
      nearestZone: null,
      // ─── Face Recognition ───────────────────────────
      faceRecognitionEnabled: false,
      faceVerified: false,
      faceConfidence: null,
      faceFailed: false,
      showDetails: false,
    };
  },
  computed: {
    formattedToday() {
      const d = new Date();
      const days = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'Mei',
        'Jun',
        'Jul',
        'Agu',
        'Sep',
        'Okt',
        'Nov',
        'Des',
      ];
      return `${days[d.getDay()]}, ${d.getDate()} ${
        months[d.getMonth()]
      } ${d.getFullYear()}`;
    },
    punchInTimeDisplay() {
      if (!this.attendanceRecordId) return '--:--';
      return this.previousAttendanceRecordTime || '--:--';
    },
    punchOutTimeDisplay() {
      return '--:--';
    },
    currentEmpNumber() {
      return this.employeeId ?? window.appGlobal?.empNumber ?? null;
    },
    isSubmitDisabled() {
      if (this.geofenceEnabled && this.geofenceStatus === 'outside')
        return true;
      if (this.faceRecognitionEnabled && !this.faceVerified) return true;
      return false;
    },
    geofenceStatusTitle() {
      switch (this.geofenceStatus) {
        case 'checking':
          return 'Memeriksa lokasi...';
        case 'inside':
          return 'Anda berada dalam zona absensi';
        case 'outside':
          return 'Anda di luar zona absensi — Absensi diblokir';
        default:
          return 'Geofence tidak aktif';
      }
    },
    previousAttendanceRecordDate() {
      if (!this.attendanceRecord?.previousRecord) return null;
      return formatDate(
        parseDate(this.attendanceRecord.previousRecord.userDate),
        this.jsDateFormat,
        {locale: this.locale},
      );
    },
    previousAttendanceRecordTime() {
      if (!this.attendanceRecord?.previousRecord) return null;
      return formatTime(
        parseTime(
          this.attendanceRecord.previousRecord.userTime,
          this.timeFormat,
        ),
        this.jsTimeFormat,
      );
    },
  },
  mounted() {
    this.getLocation();
    // Camera will be handled by FaceRecognition component if enabled
  },
  beforeUnmount() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }
  },
  beforeMount() {
    this.isLoading = true;
    this.loadConfig()
      .then(() => this.setCurrentDateTime())
      .then(() => {
        !this.date &&
          !this.isEditable &&
          setClockInterval(this.setCurrentDateTime, 60000);
        let url = '/api/v2/attendance/records/latest';
        if (this.employeeId) {
          url = `/api/v2/attendance/records/latest?empNumber=${this.employeeId}`;
        }
        return this.attendanceRecordId
          ? this.http.request({method: 'GET', url})
          : null;
      })
      .then((response) => {
        if (response) {
          const {data} = response.data;
          this.attendanceRecord.previousRecord = data.punchIn;
        }
      })
      .then(() => {
        this.previousRecordTimezone = getStandardTimezone(
          this.attendanceRecord.previousRecord?.offset,
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    // ─── Config Loading ────────────────────────────────────────────────
    async loadConfig() {
      try {
        const res = await this.configHttp.getAll();
        const config = res.data.data;
        this.geofenceEnabled = config.geofencingEnabled ?? false;
        this.faceRecognitionEnabled = config.faceRecognitionEnabled ?? false;
      } catch {
        // Config load failed, continue without features
      }
    },

    // ─── Geofence ──────────────────────────────────────────────────────
    getLocation() {
      if (!navigator.geolocation) {
        this.locationError = 'Browser Anda tidak mendukung Geolocation.';
        if (this.geofenceEnabled) this.geofenceStatus = 'outside';
        return;
      }
      this.geofenceStatus = 'checking';
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          this.attendanceRecord.latitude = position.coords.latitude;
          this.attendanceRecord.longitude = position.coords.longitude;
          this.locationError = null;
          if (this.geofenceEnabled) {
            await this.validateGeofence(
              position.coords.latitude,
              position.coords.longitude,
            );
          }
        },
        (error) => {
          this.locationError = 'Gagal mendapatkan lokasi: ' + error.message;
          if (this.geofenceEnabled) this.geofenceStatus = 'outside';
        },
        {enableHighAccuracy: true, timeout: 10000, maximumAge: 0},
      );
    },

    async validateGeofence(lat, lng) {
      try {
        const res = await this.http.request({
          method: 'GET',
          url: '/api/v2/attendance/geofence/validate',
          params: {latitude: lat, longitude: lng},
        });
        const {valid, nearestZone, zonesCount} = res.data.data;

        if (zonesCount === 0) {
          this.geofenceStatus = 'disabled';
        } else {
          this.geofenceStatus = valid ? 'inside' : 'outside';
          this.nearestZone = nearestZone;
        }
      } catch {
        this.geofenceStatus = 'disabled';
      }
    },

    // ─── Face Recognition Callbacks ────────────────────────────────────
    onFaceVerified(confidence) {
      this.faceVerified = true;
      this.faceConfidence = confidence;
      this.faceFailed = false;
    },

    onFaceFailed() {
      this.faceVerified = false;
      this.faceFailed = true;
      this.$toast.warn?.({
        title: 'Verifikasi Wajah Gagal',
        message: 'Absensi tidak diizinkan.',
      });
    },

    onNoDescriptor() {
      // No face enrolled — block attendance
      this.faceVerified = false;
      this.$toast.warn?.({
        title: 'Wajah Belum Terdaftar',
        message: 'Hubungi admin untuk mendaftarkan wajah Anda.',
      });
    },

    onCameraError(err) {
      console.warn('[RecordAttendance] Camera error:', err);
    },

    // ─── Save ──────────────────────────────────────────────────────────
    onSave() {
      // Final safety checks
      if (this.geofenceEnabled && this.geofenceStatus === 'outside') {
        this.$toast.warn?.({
          title: 'Di luar zona absensi',
          message: 'Absensi tidak diizinkan dari lokasi ini.',
        });
        return;
      }
      if (this.faceRecognitionEnabled && !this.faceVerified) {
        this.$toast.warn?.({
          title: 'Wajah tidak terverifikasi',
          message: 'Silakan verifikasi wajah terlebih dahulu.',
        });
        return;
      }

      this.isLoading = true;

      const timezone = guessTimezone();
      // Capture photo from FaceRecognition component if available
      const photoBase64 =
        this.$refs.faceRecognitionRef?.capturePhoto?.() ?? null;

      this.http
        .request({
          method: this.attendanceRecordId ? 'PUT' : 'POST',
          data: {
            date: this.attendanceRecord.date,
            time: this.attendanceRecord.time,
            note: this.attendanceRecord.note,
            timezoneOffset:
              this.attendanceRecord.timezone?._offset ?? timezone.offset,
            timezoneName: this.attendanceRecord.timezone?.id ?? timezone.name,
            latitude: this.attendanceRecord.latitude
              ? String(this.attendanceRecord.latitude)
              : null,
            longitude: this.attendanceRecord.longitude
              ? String(this.attendanceRecord.longitude)
              : null,
            photo: photoBase64,
          },
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.employeeId
            ? navigate('/attendance/viewAttendanceRecord', undefined, {
                employeeId: this.employeeId,
                date: this.date,
              })
            : reloadPage();
        });
    },

    setCurrentDateTime() {
      return new Promise((resolve, reject) => {
        this.http
          .request({method: 'GET', url: '/api/v2/attendance/current-datetime'})
          .then((res) => {
            const {utcDate, utcTime} = res.data.data;
            const currentDate = parseDate(
              `${utcDate} ${utcTime} +00:00`,
              'yyyy-MM-dd HH:mm xxx',
            );
            this.attendanceRecord.date =
              this.date ?? formatDate(currentDate, 'yyyy-MM-dd');
            this.attendanceRecord.time = formatDate(currentDate, 'HH:mm');
            resolve();
          })
          .catch((error) => reject(error));
      });
    },

    validateDate() {
      if (!this.attendanceRecord.date || !this.attendanceRecord.time)
        return true;
      if (parseDate(this.attendanceRecord.date) === null) return true;
      const tzOffset = (new Date().getTimezoneOffset() / 60) * -1;
      return new Promise((resolve) => {
        this.http
          .request({
            method: 'GET',
            url: `/api/v2/attendance/${
              this.attendanceRecordId ? 'punch-out' : 'punch-in'
            }/overlaps`,
            params: {
              date: this.attendanceRecord.date,
              time: this.attendanceRecord.time,
              timezoneOffset:
                this.attendanceRecord.timezone?._offset ?? tzOffset,
              empNumber: this.employeeId,
            },
            validateStatus: (status) =>
              (status >= 200 && status < 300) || status === 400,
          })
          .then((res) => {
            const {data, error} = res.data;
            if (error) return resolve(error.message);
            return data.valid === true
              ? resolve(true)
              : resolve(this.$t('attendance.overlapping_records_found'));
          });
      });
    },
  },
};
</script>

<style src="./record-attendance.scss" lang="scss" scoped></style>
